import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Socket } from 'socket.io-client';
import socketService from '../services/socketService';
import useRedux from '../hooks/useRedux';
import { RootState } from '../redux/store';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    isConnecting: boolean;
    connect: () => Promise<void>;
    disconnect: () => void;
    emit: (event: string, data?: any) => void;
    emitWithAck: (event: string, data?: any, callback?: (response: any) => void) => void;
    emitWithPromise: (event: string, data?: any, timeout?: number) => Promise<any>;
    on: (event: string, callback: (...args: any[]) => void) => void;
    off: (event: string, callback?: (...args: any[]) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
    children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const { appSelector } = useRedux();
    const { user: loggedInUser } = appSelector((state: RootState) => ({
        user: state.Auth.user,
    }));

    console.log('loggedInUser', loggedInUser);

    const connect = async () => {
        if (!loggedInUser?.token) {
            console.warn('No user token available for socket connection');
            return;
        }

        if (isConnected || isConnecting) {
            return;
        }

        setIsConnecting(true);
        try {
            const socketInstance = await socketService.connect(loggedInUser.token);
            setSocket(socketInstance);
            setIsConnected(true);
        } catch (error) {
            console.error('Failed to connect to socket:', error);
        } finally {
            setIsConnecting(false);
        }
    };

    const disconnect = () => {
        socketService.disconnect();
        setSocket(null);
        setIsConnected(false);
        setIsConnecting(false);
    };

    const emit = (event: string, data?: any) => {
        socketService.emit(event, data);
    };

    const emitWithAck = (event: string, data?: any, callback?: (response: any) => void) => {
        socketService.emitWithAck(event, data, callback);
    };

    const emitWithPromise = (event: string, data?: any, timeout?: number) => {
        return socketService.emitWithPromise(event, data, timeout);
    };

    const on = (event: string, callback: (...args: any[]) => void) => {
        console.log('on', event);
        socketService.on(event, callback);
    };

    const off = (event: string, callback?: (...args: any[]) => void) => {
        socketService.off(event, callback);
    };

    // Auto-connect when user logs in
    useEffect(() => {
        console.log('loggedInUser', loggedInUser, isConnected, isConnecting);
        if (loggedInUser?.token && !isConnected && !isConnecting) {
            connect();
        }
    }, [loggedInUser, isConnected, isConnecting]);

    // Auto-disconnect when user logs out
    useEffect(() => {
        if (!loggedInUser?.token && isConnected) {
            disconnect();
        }
    }, [loggedInUser?.token, isConnected]);

    // Update connection status when socket changes
    useEffect(() => {
        if (socket) {
            const handleConnect = () => setIsConnected(true);
            const handleDisconnect = () => setIsConnected(false);

            socket.on('connect', handleConnect);
            socket.on('disconnect', handleDisconnect);

            return () => {
                socket.off('connect', handleConnect);
                socket.off('disconnect', handleDisconnect);
            };
        }
    }, [socket]);

    const value: SocketContextType = {
        socket,
        isConnected,
        isConnecting,
        connect,
        disconnect,
        emit,
        emitWithAck,
        emitWithPromise,
        on,
        off,
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (): SocketContextType => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
}; 